import { DeleteFilled } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Divider, Empty, Form, Modal } from "antd";
import { ColumnsType } from "antd/lib/table";
import SortableTable from "components/SortableTable";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { Rule, RuleUIAction, RuleUICondition, RuleUIModel } from "models/rules";
import { useEffect, useRef, useState } from "react";
import NewRuleForm, { BlankSlateRule, NewRuleFormRef } from "../WebsiteUpdates/Rules/NewRuleForm";

interface CardTitleRulesModalProps {
    open: boolean;
    onCancel: () => void;
    onComplete: () => Promise<void>;
}

const useCardTitleRuleActions = () => {
    const getDesc = useAuthenticatedRequestCreator<{ description: string }, Rule>(
        (rule) => ({
            method: 'post',
            url: '/card-titling/rules/description',
            data: rule
        })
    );

    const appendAndRunNewRules = useAuthenticatedRequestCreator<void, Rule[]>(
        (rules) => ({
            method: 'post',
            url: '/card-titling/rules/save-and-run',
            data: rules
        })
    );

    const { mutateAsync: getDescription, isLoading: isGettingDescription } = useMutation(getDesc);

    const { mutateAsync: saveAndRunRules, isLoading: isSavingAndRunning } = useMutation(appendAndRunNewRules);

    return {
        getDescription,
        saveAndRunRules,
        isLoading: isGettingDescription || isSavingAndRunning
    };
}

const CardTitleRulesModal = ({ open, onCancel, onComplete }: CardTitleRulesModalProps) => {
    const [form] = Form.useForm<Rule>();
    const [rules, setRules] = useState<Rule[]>([]);
    const [current, setCurrent] = useState<RuleUIModel>({ ...BlankSlateRule });
    const resetRef = useRef<NewRuleFormRef>(null);
    const [key, setKey] = useState<string>(crypto.randomUUID());
    const { getDescription, saveAndRunRules, isLoading } = useCardTitleRuleActions();

    const canAppend = current.conditions.length > 0 && current.action !== RuleUIAction.Unknown;

    // for some reason, the form doesn't properly reset after saving a rule, so reset the form
    // again when the component key changes
    useEffect(() => {
        form.resetFields();
    }, [key, form]);

    const closeModal = () => {
        resetForm();
        setRules([]);
        onCancel();
    }

    const saveAndRun = async () => {
        await saveAndRunRules(rules);
        await onComplete();
        resetForm();
        setRules([]);
    }

    const resetForm = () => {
        resetRef.current!.reset();
        form.resetFields(['action', 'conditions']);
        setCurrent(_ => ({ ...BlankSlateRule }));
    }

    const reOrderRules = (r: Rule, index: number): Rule =>
        ({ ...r, ordinal: index + 1 });

    const onCurrentRuleChange = (conditions: RuleUICondition[], action: RuleUIAction) => {
        setCurrent(cur => ({ ...cur, conditions, action }));
    }

    const appendRule = async () => {
        const ruleFromForm = await form.validateFields();
        const { description } = await getDescription(ruleFromForm);

        const newRule = {
            ...ruleFromForm,
            id: crypto.randomUUID(),
            ordinal: rules.reduce((v, r) => Math.max(v, r.ordinal), 0) + 1,
            description
        };

        resetForm();
        setRules(cur => [...cur, newRule]);
        setKey(crypto.randomUUID());
    }

    const columns: ColumnsType<Rule> = [
        {
            key: 'actions',
            render: (_, rule) => <Button danger
                icon={<DeleteFilled />}
                onClick={async () => setRules(cur => [
                    ...cur.filter(r => r.id !== rule.id).map(reOrderRules)
                ])}
            />,
            width: 50,
        },
        {
            title: 'Order',
            dataIndex: 'ordinal',
            width: 100,
            align: 'center',
            render: v => `#${v}`
        },
        {
            title: 'Rule',
            dataIndex: 'description'
        }
    ]

    return <Modal
        title="Create & Run New Card Title Rules"
        width='700px'
        open={open}
        onCancel={closeModal}
        onOk={saveAndRun}
        okText="Save & Run New Rules"
        okButtonProps={{ disabled: rules.length === 0 }}
        closable={false}
        maskClosable={false}
        destroyOnClose
        keyboard={false}
        confirmLoading={isLoading}
    >
        <NewRuleForm
            key={key}
            form={form}
            onChange={onCurrentRuleChange}
            ref={resetRef}
        />
        {canAppend && <Button
            block
            type="primary"
            style={{ marginTop: '20px' }}
            onClick={appendRule}
        >
            Append Rule
        </Button>}
        <Divider />
        <SortableTable
            columns={columns}
            data={rules}
            isLoading={isLoading}
            onReorder={async (items) => setRules(items.map(reOrderRules))}
            noData={<Empty description="No new rules have been created" />}
        />
    </Modal>
}

export default CardTitleRulesModal;